
import React from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import styles from "./AgentAuditDetail.module.scss";
import { DataService } from "../../store/DataService";
import TicketDetail from "../../components/TicketDetail";

const AgentAuditDetail = (props) => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { uaid: paramUAID, date: paramDate } = useParams();
    const [auditDate, setAuditDate] = React.useState(paramDate || new Date().toISOString().split('T')[0]);
    const [agentList, setAgentList] = React.useState([]);
    const [selectedUAID, setSelectedUAID] = React.useState(paramUAID || '0');
    const [auditData, setAuditData] = React.useState([]);
    const [summaryData, setSummaryData] = React.useState(null);
    const [timelineData, setTimelineData] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [selectedTicket, setSelectedTicket] = React.useState(null);
    const [expandedGroups, setExpandedGroups] = React.useState(new Set());

    // Reset expanded groups when timeline data or selected agent changes
    React.useEffect(() => {
        setExpandedGroups(new Set());
    }, [timelineData, selectedUAID]);

    const groupTimelineEvents = React.useCallback((rawEvents) => {
        if (!rawEvents || rawEvents.length === 0) return [];
        
        // Helper to convert HH:MM:SS to seconds
        const timeToSeconds = (timeStr) => {
            if (!timeStr) return 0;
            const parts = timeStr.split(':');
            return parseInt(parts[0], 10) * 3600 + parseInt(parts[1], 10) * 60 + parseInt(parts[2] || 0, 10);
        };
        
        // 1. Pre-filter out duplicate/redundant logins
        const filteredEvents = [];
        let lastActiveTime = null;
        let lastLogoutTime = null;
        
        for (const ev of rawEvents) {
            if (ev.title === "Login") {
                const currentSec = timeToSeconds(ev.time);
                
                // If they logged in, but we had other activity in the last 15 minutes (900 seconds) 
                // and there was no logout since that activity, suppress the duplicate login node.
                if (lastActiveTime !== null && (currentSec - lastActiveTime) < 900) {
                    if (lastLogoutTime === null || lastLogoutTime < lastActiveTime) {
                        // Suppress duplicate login
                        continue;
                    }
                }
                
                lastActiveTime = currentSec;
            } else if (ev.title === "Logout") {
                const currentSec = timeToSeconds(ev.time);
                lastLogoutTime = currentSec;
                lastActiveTime = currentSec;
            } else {
                const currentSec = timeToSeconds(ev.time);
                lastActiveTime = currentSec;
            }
            
            filteredEvents.push(ev);
        }
        
        // 2. Perform sequential grouping on filtered events
        const formatDuration = (seconds) => {
            if (seconds < 0) return "";
            const h = Math.floor(seconds / 3600);
            const m = Math.floor((seconds % 3600) / 60);
            const s = seconds % 60;
            
            const parts = [];
            if (h > 0) parts.push(`${h}h`);
            if (m > 0) parts.push(`${m}m`);
            if (s > 0 || parts.length === 0) parts.push(`${s}s`);
            return parts.join(' ');
        };

        const grouped = [];
        let currentGroup = null;

        const pushCurrentGroup = () => {
            if (!currentGroup) return;
            const durationSec = timeToSeconds(currentGroup.endTime) - timeToSeconds(currentGroup.startTime);
            currentGroup.durationStr = formatDuration(durationSec);
            grouped.push(currentGroup);
        };
        
        for (const ev of filteredEvents) {
            let ticketId = null;
            const ticketMatch = ev.detail ? ev.detail.match(/Ticket #(\d+)/i) : null;
            if (ticketMatch) {
                ticketId = ticketMatch[1];
            }
            
            if (ev.title === "Page View") {
                if (currentGroup && currentGroup.type === "navigation") {
                    currentGroup.events.push(ev);
                    currentGroup.endTime = ev.time;
                } else {
                    pushCurrentGroup();
                    currentGroup = {
                        type: "navigation",
                        title: "Portal Navigation",
                        startTime: ev.time,
                        endTime: ev.time,
                        events: [ev],
                        color: "blue"
                    };
                }
            } else if (ticketId) {
                if (currentGroup && currentGroup.type === "ticket" && currentGroup.ticketId === ticketId) {
                    currentGroup.events.push(ev);
                    currentGroup.endTime = ev.time;
                } else {
                    pushCurrentGroup();
                    currentGroup = {
                        type: "ticket",
                        ticketId: ticketId,
                        title: `Worked on Ticket #${ticketId}`,
                        startTime: ev.time,
                        endTime: ev.time,
                        events: [ev],
                        color: "orange"
                    };
                }
            } else {
                pushCurrentGroup();
                currentGroup = null;
                grouped.push({
                    type: "single",
                    title: ev.title,
                    startTime: ev.time,
                    events: [ev],
                    color: ev.color,
                    detail: ev.detail
                });
            }
        }
        
        pushCurrentGroup();
        
        return grouped;
    }, []);

    const scrollToTicket = React.useCallback((ticketId) => {
        if (!ticketId) return;
        const element = document.getElementById(`ticket-row-${ticketId}`);
        if (element) {
            // Find the scroll container (leftColumn)
            const container = element.closest(`.${styles.leftColumn}`);
            if (!container) return;

            // Clear any active highlight styles inside the container first
            container.querySelectorAll('td').forEach(cell => {
                cell.style.backgroundColor = '';
                cell.style.borderTop = '';
                cell.style.borderBottom = '';
            });

            element.scrollIntoView({ behavior: 'smooth', block: 'center' });
            
            // Calculate scroll distance to dynamically scale the highlight delay
            const currentScrollTop = container.scrollTop;
            const elementOffsetTop = element.offsetTop;
            const containerHeight = container.clientHeight;
            const targetScrollTop = elementOffsetTop - (containerHeight / 2);
            const distance = Math.abs(currentScrollTop - targetScrollTop);
            
            // browser smooth scroll is ~1-2px per ms. Scale delay: min 200ms, max 800ms.
            const scrollDelay = Math.min(800, Math.max(200, Math.floor(distance * 0.6)));

            setTimeout(() => {
                const cells = element.querySelectorAll('td');
                
                // Apply highlight directly
                cells.forEach(cell => {
                    cell.style.transition = 'background-color 0.4s ease, border-color 0.4s ease';
                    cell.style.backgroundColor = '#fff3cd'; // Warm amber/yellow highlight
                    cell.style.borderTop = '2px solid #ffc107';
                    cell.style.borderBottom = '2px solid #ffc107';
                });
                
                // Keep the highlight visible for exactly 1.2 seconds, then smoothly fade it out
                setTimeout(() => {
                    cells.forEach(cell => {
                        cell.style.backgroundColor = '';
                        cell.style.borderTop = '';
                        cell.style.borderBottom = '';
                    });
                }, 1200);
            }, scrollDelay);
        } else {
            console.warn(`Row for ticket ${ticketId} not found in DOM`);
        }
    }, []);

    const renderEventDetail = React.useCallback((detail) => {
        if (!detail) return "";
        const ticketMatch = detail.match(/Ticket #(\d+)/i);
        if (ticketMatch) {
            const ticketId = ticketMatch[1];
            const parts = detail.split(ticketMatch[0]);
            return (
                <span>
                    {parts[0]}
                    <span 
                        className={styles.timelineTicketLink}
                        onClick={(e) => {
                            e.stopPropagation();
                            scrollToTicket(ticketId);
                        }}
                    >
                        Ticket #{ticketId}
                    </span>
                    {parts[1]}
                </span>
            );
        }
        return detail;
    }, [scrollToTicket]);

    const fetchAuditData = React.useCallback(async (uaid, date) => {
        if (uaid === '0') {
            setAuditData([]);
            setSummaryData(null);
            return;
        }
        setLoading(true);
        const ds = new DataService({ guid: props.globals.sessionguid });
        try {
            const data = await ds.getagentaudit(uaid, date);
            setLoading(false);
            if (data.ERROR) {
                alert(data.ERROR);
            } else {
                setAuditData(data.TICKETS || []);
                setSummaryData(data.SUMMARY || null);
                setTimelineData(data.TIMELINE || []);
            }
        } catch (err) {
            console.error("Failed to fetch audit data:", err);
            setLoading(false);
        }
    }, [props.globals.sessionguid]);

    // Fetch agent list when date changes
    React.useEffect(() => {
        if (!props.globals.sessionguid) return;

        const fetchAgents = async () => {
            const ds = new DataService({ guid: props.globals.sessionguid });
            try {
                const data = await ds.getagentlist(auditDate);
                if (!data.ERROR) {
                    setAgentList(data.AGENTS || []);
                }
            } catch (err) {
                console.error("Failed to fetch agent list:", err);
            }
        };
        fetchAgents();
    }, [auditDate, props.globals.sessionguid]);

    // Trigger initial fetch if we have params
    React.useEffect(() => {
        if (selectedUAID !== '0') {
            fetchAuditData(selectedUAID, auditDate);
        }
    }, [selectedUAID, auditDate, fetchAuditData]);

    const handleDateChange = (e) => {
        setAuditDate(e.target.value);
        // We don't clear selectedUAID here, but the fetchAgents useEffect will run
        // and the fetchAuditData useEffect will also run because auditDate changed
    };

    const handleAgentChange = (e) => {
        const uaid = e.target.value;
        setSelectedUAID(uaid);
        // fetchAuditData will be triggered by useEffect
    };

    const handleTicketClick = (ticketid) => {
        const ds = new DataService({ guid: props.globals.sessionguid });
        ds.readticketdetail(props.globals.useraccountid, ticketid).then((data) => {
            if (data.ERROR) {
                alert(data.ERROR);
            } else {
                setSelectedTicket(data.TICKET);
            }
        });
    };

    const closeTicketDetail = () => {
        setSelectedTicket(null);
    };

    // Ensure duration is always in HH:MM:SS format for Excel compatibility
    const ensureHHMMSS = (durationStr) => {
        if (!durationStr || typeof durationStr !== 'string') return "00:00:00";
        const parts = durationStr.split(':');
        if (parts.length === 2) {
            return `00:${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}`;
        }
        if (parts.length === 3) {
            return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}:${parts[2].padStart(2, '0')}`;
        }
        return durationStr;
    };

    // Convert duration HH:MM:SS or MM:SS to seconds
    const durationToSeconds = (durationStr) => {
        if (!durationStr || typeof durationStr !== 'string') return 0;
        const parts = durationStr.split(':');
        if (parts.length === 2) {
            return (parseInt(parts[0], 10) * 60) + parseInt(parts[1], 10);
        }
        if (parts.length === 3) {
            return (parseInt(parts[0], 10) * 3600) + (parseInt(parts[1], 10) * 60) + parseInt(parts[2], 10);
        }
        return 0;
    };

    const formatSeconds = (totalSeconds) => {
        const sec = Math.round(totalSeconds || 0);
        const h = Math.floor(sec / 3600);
        const m = Math.floor((sec % 3600) / 60);
        const s = sec % 60;
        return [h, m, s].map(v => v.toString().padStart(2, '0')).join(':');
    };

    const exportToCSV = () => {
        if (auditData.length === 0) return;

        const csvRows = [];
        const agentName = agentList.find(a => a.uaid === selectedUAID)?.name || selectedUAID;

        // 1. Calculate state totals from granular auditData
        const stateTotals = {};
        auditData.forEach(row => {
            const state = row.statename || "Unknown";
            const duration = row.duration !== undefined ? row.duration : durationToSeconds(row.duration_str);
            stateTotals[state] = (stateTotals[state] || 0) + duration;
        });
        const sortedStates = Object.entries(stateTotals).sort((a, b) => b[1] - a[1]);

        // 2. Build Report Headers
        csvRows.push(`Agent Audit Report for ${agentName} on ${auditDate}`);
        csvRows.push("");

        // 3. Build Side-by-Side Summary Section
        const leftSide = [];
        if (summaryData) {
            leftSide.push(["Tickets Assigned", summaryData.total_tickets, ""]);
            leftSide.push(["Tickets Completed", summaryData.completed_tickets, ""]);
            leftSide.push(["Tickets Uncompleted", summaryData.uncompleted_tickets || 0, ""]);
            leftSide.push(["Timeouts", summaryData.timeouts, ""]);
            leftSide.push(["Active", summaryData.active || 0, ""]);
            leftSide.push(["Avg Handle Time (AHT)", ensureHHMMSS(summaryData.aht_str), summaryData.aht || durationToSeconds(summaryData.aht_str)]);
            leftSide.push(["Total Logged In", ensureHHMMSS(summaryData.total_login), summaryData.seconds_login || durationToSeconds(summaryData.total_login)]);
            leftSide.push(["Total Reserved", ensureHHMMSS(summaryData.total_reserved), summaryData.seconds_reserved || durationToSeconds(summaryData.total_reserved)]);
            leftSide.push(["Total Inprogress", ensureHHMMSS(summaryData.total_inprogress), summaryData.seconds_inprogress || durationToSeconds(summaryData.total_inprogress)]);
            leftSide.push(["Total Wrapup", ensureHHMMSS(summaryData.total_wrapup), summaryData.seconds_wrapup || durationToSeconds(summaryData.total_wrapup)]);
            leftSide.push(["Total Idle Time", ensureHHMMSS(summaryData.total_idle), summaryData.seconds_idle || durationToSeconds(summaryData.total_idle)]);
            leftSide.push(["Total Unavailable", ensureHHMMSS(summaryData.total_unavailable), summaryData.seconds_unavailable || durationToSeconds(summaryData.total_unavailable)]);
            
            if (summaryData.state_totals) {
                summaryData.state_totals.filter(st => !["QUEUE", "IDLE", "WRAPUP", "UNAVAILABLE", "RESERVED", "INPROGRESS"].includes(st.name)).forEach(st => {
                    leftSide.push([`Total ${st.name}`, ensureHHMMSS(st.duration_str), st.seconds || durationToSeconds(st.duration_str)]);
                });
            }
        }

        const rightSide = [["State Breakdown (Calculated)", "Duration (HH:MM:SS)", "Duration (Seconds)"]];
        sortedStates.forEach(([state, seconds]) => {
            rightSide.push([state, formatSeconds(seconds), seconds]);
        });

        // Merge left and right sides
        csvRows.push("SUMMARY TOTALS,,, ,STATE TOTALS (FROM DETAIL)");
        const maxSummaryRows = Math.max(leftSide.length, rightSide.length);
        for (let i = 0; i < maxSummaryRows; i++) {
            const left = leftSide[i] || ["", "", ""];
            const right = rightSide[i] || ["", "", ""];
            csvRows.push([...left, "", ...right].join(","));
        }
        csvRows.push("");

        // 4. Add Ticket Detail Table
        csvRows.push("TICKET DETAILS");
        const headers = ["Ticket #", "Intent", "State", "Started", "Duration (HH:MM:SS)", "Duration (Seconds)", "Reason"];
        csvRows.push(headers.join(","));
        
        auditData.forEach(row => {
            const rowData = [
                row.ticketid || "",
                `"${(row.tickettype || "").replace(/"/g, '""')}"`,
                `"${(row.statename || "").replace(/"/g, '""')}"`,
                formatTime(row.eventdate),
                ensureHHMMSS(row.duration_str),
                row.duration !== undefined ? row.duration : durationToSeconds(row.duration_str),
                `"${(row.reason || "").replace(/"/g, '""')}"`
            ];
            csvRows.push(rowData.join(","));
        });

        const csvString = csvRows.join("\n");
        const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.setAttribute("href", url);
        link.setAttribute("download", `AgentAudit_${agentName}_${auditDate}.csv`);
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    // Format Started time (HH:MM:SS)
    const formatTime = (fullDate) => {
        if (!fullDate || !fullDate.includes(' ')) return fullDate;
        return fullDate.split(' ')[1];
    };

    return (
        <React.Fragment>
            <div className={styles.title}>Agent Audit Detail</div>
            <div className={styles.page}>
                <div className={styles.filterbar}>
                    <label className={styles.formlabel}>
                        Select Date:
                        <input 
                            className={styles.forminput}
                            type="date" 
                            value={auditDate} 
                            onChange={handleDateChange} 
                        />
                    </label>

                    <label className={styles.formlabel}>
                        Select Agent:
                        <select 
                            className={styles.formselect}
                            value={selectedUAID} 
                            onChange={handleAgentChange} 
                        >
                            <option value="0">-- Select Agent ({agentList.length} found) --</option>
                            {agentList.map(agent => (
                                <option key={agent.uaid} value={agent.uaid}>{agent.name}</option>
                            ))}
                        </select>
                    </label>
                    <button 
                        onClick={() => {
                            const sort = searchParams.get("sort");
                            const dir = searchParams.get("dir");
                            const sortQuery = sort ? `?sort=${sort}&dir=${dir}` : "";
                            navigate(`/agentaudit/${auditDate}${sortQuery}`);
                        }} 
                        style={{marginLeft: 'auto', padding: '5px 15px'}}
                    >
                        Summary
                    </button>
                    <button 
                        onClick={exportToCSV}
                        disabled={auditData.length === 0}
                        style={{
                            marginLeft: '10px', 
                            padding: '5px 15px', 
                            backgroundColor: auditData.length === 0 ? '#ccc' : '#28a745', 
                            color: 'white', 
                            border: 'none', 
                            borderRadius: '4px', 
                            cursor: auditData.length === 0 ? 'default' : 'pointer'
                        }}
                    >
                        Export CSV
                    </button>
                </div>

                {summaryData && (
                    <div className={styles.summarycontainer}>
                        <div className={styles.summarycard}>
                            <div className={styles.summarytitle}>Tickets Assigned</div>
                            <div className={styles.summaryvalue}>{summaryData.total_tickets}</div>
                        </div>
                        <div className={styles.summarycard}>
                            <div className={styles.summarytitle}>Tickets Completed</div>
                            <div className={styles.summaryvalue} style={{color: '#28a745'}}>{summaryData.completed_tickets}</div>
                        </div>
                        <div className={styles.summarycard}>
                            <div className={styles.summarytitle}>Tickets Uncompleted</div>
                            <div className={styles.summaryvalue} style={{color: '#e67e22'}}>{summaryData.uncompleted_tickets || 0}</div>
                        </div>
                        <div className={styles.summarycard}>
                            <div className={styles.summarytitle}>Timeouts</div>
                            <div className={styles.summaryvalue} style={{color: summaryData.timeouts > 0 ? 'red' : 'inherit'}}>{summaryData.timeouts}</div>
                        </div>
                        <div className={styles.summarycard}>
                            <div className={styles.summarytitle}>Active</div>
                            <div className={styles.summaryvalue} style={{color: summaryData.active > 0 ? '#0066cc' : 'inherit'}}>{summaryData.active || 0}</div>
                        </div>
                        <div className={styles.summarycard}>
                            <div className={styles.summarytitle}>AHT (Avg Handle)</div>
                            <div className={styles.summaryvalue}>{summaryData.aht_str || "00:00"}</div>
                        </div>
                        <div className={styles.summarycard}>
                            <div className={styles.summarytitle}>Total Logged In</div>
                            <div className={styles.summaryvalue}>{summaryData.total_login || "00:00:00"}</div>
                        </div>
                        <div className={styles.summarycard}>
                            <div className={styles.summarytitle}>Total Reserved</div>
                            <div className={styles.summaryvalue}>{summaryData.total_reserved || "00:00"}</div>
                        </div>
                        <div className={styles.summarycard}>
                            <div className={styles.summarytitle}>Total Inprogress</div>
                            <div className={styles.summaryvalue}>{summaryData.total_inprogress || "00:00"}</div>
                        </div>
                        <div className={styles.summarycard}>
                            <div className={styles.summarytitle}>Total Wrapup</div>
                            <div className={styles.summaryvalue}>{summaryData.total_wrapup || "00:00"}</div>
                        </div>
                        <div className={styles.summarycard}>
                            <div className={styles.summarytitle}>Total Idle</div>
                            <div className={styles.summaryvalue}>{summaryData.total_idle || "00:00"}</div>
                        </div>
                        <div className={styles.summarycard}>
                            <div className={styles.summarytitle}>Total Unavailable</div>
                            <div className={styles.summaryvalue}>{summaryData.total_unavailable || "00:00"}</div>
                        </div>
                        {summaryData.state_totals && summaryData.state_totals.filter(st => !["QUEUE", "IDLE", "WRAPUP", "UNAVAILABLE", "RESERVED", "INPROGRESS"].includes(st.name)).map(st => (
                             <div key={st.name} className={styles.summarycard}>
                                <div className={styles.summarytitle}>Total {st.name}</div>
                                <div className={styles.summaryvalue}>{st.duration_str}</div>
                            </div>
                        ))}
                    </div>
                )}

                {loading && <div className={styles.loading}>Loading audit data...</div>}

                {!loading && auditData.length > 0 && (
                    <div className={styles.reportcontainer}>
                        <div className={styles.leftColumn}>
                            <table className={styles.tickettable}>
                                <thead>
                                    <tr>
                                        <th style={{width: '70px'}}>Ticket #</th>
                                        <th style={{width: '110px'}}>Intent</th>
                                        <th style={{width: '120px'}}>State</th>
                                        <th style={{width: '100px'}}>Started</th>
                                        <th style={{width: '100px'}}>Duration</th>
                                        <th style={{width: '160px'}}>Reason</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {auditData.map((row, index) => {
                                        // Find the last row with a non-empty ticketid before the current index
                                        let prevTicketId = "";
                                        for (let i = index - 1; i >= 0; i--) {
                                            if (auditData[i].ticketid) {
                                                prevTicketId = auditData[i].ticketid;
                                                break;
                                            }
                                        }
                                        const isNewTicket = index === 0 || (row.ticketid && row.ticketid !== prevTicketId);
                                        const showSeparator = index > 0 && isNewTicket;

                                        // Determine if this specific row represents the "Active" state of the ticket
                                        const isLastRowForTicket = index === auditData.length - 1 || row.ticketid !== auditData[index + 1].ticketid;
                                        const isActiveRow = isLastRowForTicket && 
                                                           row.statename !== "CLOSED" && 
                                                           row.statename !== "TIMEOUT" && 
                                                           row.statename !== "IDLE" &&
                                                           !auditData.slice(index - (auditData.filter(t => t.ticketid === row.ticketid).length - 1), index + 1).some(t => t.statename === "CLOSED" || t.statename === "TIMEOUT" || t.statename === "QUEUE");

                                        // A row is abandoned if it's the LAST row for a ticket and it leads to QUEUE without ever being CLOSED or TIMEOUT
                                        const isAbandonedRow = isLastRowForTicket && 
                                                             row.statename === "QUEUE" && 
                                                             !auditData.filter(t => t.ticketid === row.ticketid).some(t => t.statename === "CLOSED" || t.statename === "TIMEOUT");

                                        return (
                                            <React.Fragment key={index}>
                                                {showSeparator && (
                                                    <tr className={styles.separator}>
                                                        <td colSpan="6"></td>
                                                    </tr>
                                                )}
                                                <tr 
                                                    id={isNewTicket ? `ticket-row-${row.ticketid}` : undefined}
                                                    className={`
                                                        ${styles.trow} 
                                                        ${row._is_idle ? styles.idlerow : ""} 
                                                        ${row._is_unavail ? styles.unavailrow : ""} 
                                                        ${isActiveRow ? styles.activerow : ""}
                                                        ${isAbandonedRow ? styles.abandonedrow : ""}
                                                     `} 
                                                    style={{ 
                                                        color: row.statename === "TIMEOUT" ? "red" : 
                                                               isAbandonedRow ? "orange" : "inherit" 
                                                    }}
                                                >
                                                    <td 
                                                        style={{ fontWeight: isNewTicket ? "bold" : "normal", cursor: isNewTicket ? "pointer" : "default", color: isNewTicket ? "#0066cc" : "inherit" }}
                                                        onClick={() => isNewTicket && handleTicketClick(row.ticketid)}
                                                    >
                                                        {isNewTicket ? row.ticketid : ""}
                                                    </td>
                                                    <td>{isNewTicket ? row.tickettype : ""}</td>
                                                    <td style={{ fontWeight: row.statename === "TIMEOUT" ? "bold" : "normal" }}>{row.statename}</td>
                                                    <td>{formatTime(row.eventdate)}</td>
                                                    <td>{row.duration_str}</td>
                                                    <td>{row.reason || ""}</td>
                                                </tr>
                                            </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>

                        <div className={styles.rightColumn}>
                            <div className={styles.timelineHeaderRow}>
                                <div className={styles.timelineTitle}>Agent Timeline</div>
                                {timelineData.length > 0 && (
                                    <div className={styles.timelineActions}>
                                        <button 
                                            className={styles.actionLink}
                                            onClick={() => {
                                                const grouped = groupTimelineEvents(timelineData);
                                                const allIndices = new Set(grouped.map((_, i) => i));
                                                setExpandedGroups(allIndices);
                                            }}
                                        >
                                            Expand All
                                        </button>
                                        <span className={styles.divider}>|</span>
                                        <button 
                                            className={styles.actionLink}
                                            onClick={() => setExpandedGroups(new Set())}
                                        >
                                            Collapse All
                                        </button>
                                    </div>
                                )}
                            </div>
                            {timelineData.length === 0 ? (
                                <div className={styles.noresults} style={{padding: "15px"}}>No activity recorded on this day.</div>
                            ) : (
                                <ul className={styles.timeline}>
                                    {groupTimelineEvents(timelineData).map((group, index) => {
                                        const isExpanded = expandedGroups.has(index);
                                        
                                        if (group.type === "single") {
                                            const ev = group.events[0];
                                            let markerClass = "";
                                            if (ev.color === "green") markerClass = styles.markerGreen;
                                            else if (ev.color === "gray") markerClass = styles.markerGray;
                                            else if (ev.color === "blue") markerClass = styles.markerBlue;
                                            else if (ev.color === "orange") markerClass = styles.markerOrange;
                                            else if (ev.color === "purple") markerClass = styles.markerPurple;
                                            else if (ev.color === "yellow") markerClass = styles.markerYellow;
                                            
                                            return (
                                                <li key={index} className={styles.timelineItem}>
                                                    <div className={`${styles.timelineMarker} ${markerClass}`}></div>
                                                     <div className={styles.timelineContent}>
                                                         <div className={styles.timelineHeader}>
                                                             <span className={styles.timelineEventTitle}>{ev.title}</span>
                                                             <span className={styles.timelineTime}>{ev.time}</span>
                                                         </div>
                                                         <div className={styles.timelineDetail}>{renderEventDetail(ev.detail)}</div>
                                                     </div>
                                                </li>
                                            );
                                        }
                                        
                                        let markerClass = "";
                                        if (group.color === "blue") markerClass = styles.markerBlue;
                                        else if (group.color === "orange") markerClass = styles.markerOrange;
                                        
                                        const eventCount = group.events.length;
                                        const timeRange = group.startTime === group.endTime ? group.startTime : `${group.startTime} - ${group.endTime}`;
                                        
                                        return (
                                            <li key={index} className={`${styles.timelineItem} ${styles.timelineGroupItem}`}>
                                                <div className={`${styles.timelineMarker} ${markerClass}`}></div>
                                                <div 
                                                    className={`${styles.timelineContent} ${styles.groupHeader}`}
                                                    onClick={() => {
                                                        setExpandedGroups(prev => {
                                                            const next = new Set(prev);
                                                            if (next.has(index)) {
                                                                next.delete(index);
                                                            } else {
                                                                next.add(index);
                                                            }
                                                            return next;
                                                        });
                                                    }}
                                                >
                                                    <div className={styles.timelineHeader}>
                                                        <span className={styles.groupTitle}>
                                                            {group.type === "navigation" ? (
                                                                <span>🧭 {group.title} <small style={{ fontWeight: 'normal', color: '#666' }}>({eventCount} clicks)</small></span>
                                                            ) : (
                                                                <span>
                                                                    🎫 Worked on Ticket{" "}
                                                                    <span 
                                                                        className={styles.timelineTicketLink}
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            scrollToTicket(group.ticketId);
                                                                        }}
                                                                    >
                                                                        #{group.ticketId}
                                                                    </span>{" "}
                                                                    <small style={{ fontWeight: 'normal', color: '#666' }}>({eventCount} actions)</small>
                                                                </span>
                                                            )}
                                                        </span>
                                                         <span className={styles.groupTimeRange}>
                                                             {timeRange}
                                                             {group.durationStr && (
                                                                 <span className={styles.durationLabel} style={{ marginLeft: "6px", background: "#e9ecef", padding: "1px 6px", borderRadius: "10px", fontWeight: "bold", fontSize: "10px", color: "#495057" }}>
                                                                     {group.durationStr}
                                                                 </span>
                                                             )}
                                                             <span className={styles.chevron}>
                                                                 {isExpanded ? " ▾" : " ▸"}
                                                             </span>
                                                         </span>
                                                    </div>
                                                </div>
                                                
                                                {isExpanded && (
                                                    <ul className={styles.nestedEvents}>
                                                        {group.events.map((ev, subIdx) => {
                                                            let subMarkerClass = "";
                                                            if (ev.color === "green") subMarkerClass = styles.markerGreen;
                                                            else if (ev.color === "gray") subMarkerClass = styles.markerGray;
                                                            else if (ev.color === "blue") subMarkerClass = styles.markerBlue;
                                                            else if (ev.color === "orange") subMarkerClass = styles.markerOrange;
                                                            else if (ev.color === "purple") subMarkerClass = styles.markerPurple;
                                                            
                                                            return (
                                                                <li key={subIdx} className={styles.nestedEventItem}>
                                                                    <div className={`${styles.nestedMarker} ${subMarkerClass}`}></div>
                                                                    <div className={styles.nestedContent}>
                                                                        <div className={styles.nestedHeader}>
                                                                            <span className={styles.nestedTitle}>{ev.title}</span>
                                                                            <span className={styles.nestedTime}>{ev.time}</span>
                                                                        </div>
                                                                        <div className={styles.nestedDetail}>{renderEventDetail(ev.detail)}</div>
                                                                    </div>
                                                                </li>
                                                            );
                                                        })}
                                                    </ul>
                                                )}
                                            </li>
                                        );
                                    })}
                                </ul>
                            )}
                        </div>
                    </div>
                )}

                {!loading && selectedUAID !== '0' && auditData.length === 0 && (
                    <div className={styles.noresults}>No audit records found for this agent on this date.</div>
                )}
            </div>
            {selectedTicket && <TicketDetail 
                                globals={props.globals} 
                                methods={props.methods} 
                                allowpriorityedit={false} 
                                fromsearch={true} 
                                disableworkflow={true}
                                ticket={selectedTicket} 
                                doClose={closeTicketDetail} 
                                closeticket={() => {}} 
                                />}
        </React.Fragment>
    );
};

export default AgentAuditDetail;
